import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IconButton, CircularProgress } from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  DoneAll as DoneAllIcon,
  Done as DoneIcon,
  InsertDriveFile as FileIcon,
  MoreHoriz as MoreHorizIcon,
} from "@mui/icons-material";

import CustomAvatar from "components/common/Avatar";
import CustomButton from "components/common/Button";
import CustomTooltip from "components/common/Tooltip";
import Menu, { MenuItem } from "components/common/Menu";
import Text from "components/common/Text";

import { chatSendMessage, chatUploadFile, chatLoadMoreMessages } from "store/actions/Chat";
import {
  emitTypingStart,
  emitTypingStop,
  emitMessageRead,
  emitAddReaction,
  emitRemoveReaction,
  emitEditMessage,
  emitDeleteMessage,
} from "services/chat/socketService";

import { getInitials, formatMessageTime, formatDateHeader, formatLastSeen } from "helpers/index";

const EMOJI_LIST = ["\uD83D\uDC4D", "\u2764\uFE0F", "\uD83D\uDE02", "\uD83D\uDE22", "\uD83D\uDE2E"];

const getGroupedReactions = (reactions) => {
  return reactions.reduce((acc, r) => {
    const group = acc[r.emoji] || [];
    return { ...acc, [r.emoji]: [...group, r] };
  }, {});
};

function MessageThread() {
  const dispatch = useDispatch();
  const {
    activeConversation,
    messages,
    messagesTotalCount,
    typingUsers,
    onlineUsers,
    lastSeenTimes,
    uploadingFile,
    contacts,
  } = useSelector((state) => state.Chat);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
  const { userId, type: userType } = loggedInUser;

  const userNameMap = useMemo(() => {
    const map = {};
    map[userId] = loggedInUser.name || "You";
    (contacts || []).forEach((c) => {
      map[c.userId] = `${c.firstName} ${c.lastName}`;
    });
    (activeConversation?.Participants || []).forEach((p) => {
      if (p.userInfo) {
        map[p.userId] = `${p.userInfo.firstName} ${p.userInfo.lastName}`;
      }
    });
    return map;
  }, [contacts, activeConversation, userId, loggedInUser.name]);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const [inputValue, setInputValue] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [fileError, setFileError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const loadingOlderRef = useRef(false);
  const prevScrollHeightRef = useRef(0);

  const conversationId = activeConversation?.conversationId;

  const otherParticipant = (activeConversation?.Participants || []).find(
    (p) => !(p.userId === userId && p.userType === userType)
  );
  const otherInfo = otherParticipant?.userInfo;
  const otherParticipantKey = otherParticipant ? `${otherParticipant.userId}_${otherParticipant.userType}` : null;
  const otherOnline = otherParticipantKey && onlineUsers[otherParticipantKey];
  const otherLastSeen = otherParticipantKey && lastSeenTimes[otherParticipantKey];

  const typingInConv = typingUsers[conversationId] || {};
  const someoneTyping = Object.keys(typingInConv).length > 0;

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (loadingOlderRef.current && container) {
      // Restore scroll position after older messages are prepended
      const newScrollHeight = container.scrollHeight;
      container.scrollTop = newScrollHeight - prevScrollHeightRef.current;
      loadingOlderRef.current = false;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (conversationId) {
      emitMessageRead(conversationId);
    }
  }, [conversationId, messages.length]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (conversationId) {
      emitTypingStart(conversationId);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        emitTypingStop(conversationId);
      }, 2000);
    }
  };

  const handleSend = () => {
    if (!inputValue.trim() && !editingMessage) return;

    if (editingMessage) {
      emitEditMessage(conversationId, editingMessage.messageId, inputValue.trim());
      setEditingMessage(null);
    } else {
      dispatch(
        chatSendMessage({
          conversationId,
          content: inputValue.trim(),
          messageType: "text",
          parentMessageId: replyTo?.messageId || null,
          pending: activeConversation?.pending,
          targetUserId: activeConversation?.targetUserId,
          targetUserType: activeConversation?.targetUserType,
        })
      );
      setReplyTo(null);
    }

    setInputValue("");
    if (conversationId) emitTypingStop(conversationId);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setFileError(`File size exceeds 10MB limit (${(file.size / (1024 * 1024)).toFixed(1)}MB)`);
      e.target.value = "";
      setTimeout(() => setFileError(null), 5000);
      return;
    }
    setFileError(null);
    dispatch(chatUploadFile({ conversationId, file }));
    e.target.value = "";
  };

  const handleLoadMore = () => {
    const container = messagesContainerRef.current;
    if (container) {
      prevScrollHeightRef.current = container.scrollHeight;
    }
    loadingOlderRef.current = true;
    dispatch(
      chatLoadMoreMessages({
        conversationId,
        limit: 50,
        offset: messages.length,
      })
    );
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleReply = () => {
    setReplyTo(selectedMessage);
    handleCloseMenu();
  };

  const handleEdit = () => {
    setEditingMessage(selectedMessage);
    setInputValue(selectedMessage.content || "");
    handleCloseMenu();
  };

  const handleDelete = () => {
    emitDeleteMessage(conversationId, selectedMessage.messageId);
    handleCloseMenu();
  };

  const handleReaction = (messageId, emoji) => {
    const msg = messages.find((m) => m.messageId === messageId);
    const alreadyReacted = (msg?.Reactions || []).some((r) => r.userId === userId && r.emoji === emoji);
    if (alreadyReacted) {
      emitRemoveReaction(conversationId, messageId, emoji);
    } else {
      emitAddReaction(conversationId, messageId, emoji);
    }
  };

  const isSentByMe = (msg) => msg.senderId === userId;

  const groupedMessages = messages.reduce((acc, msg) => {
    const date = new Date(msg.createdAt).toDateString();
    const group = acc[date] || [];
    return { ...acc, [date]: [...group, msg] };
  }, {});

  const getStatusText = () => {
    if (otherOnline) return "Online";
    if (otherLastSeen) return formatLastSeen(otherLastSeen);
    return "Offline";
  };

  const getFileUrl = (attachment) => {
    return attachment?.fileUrl || null;
  };

  const renderMessageContent = (msg) => {
    if (msg.isDeleted) {
      return <span className="message-bubble__deleted">This message was deleted</span>;
    }
    if (msg.messageType === "image" && msg.Attachments?.[0]) {
      const imageUrl = getFileUrl(msg.Attachments[0]);
      return (
        <a href={imageUrl} target="_blank" rel="noopener noreferrer">
          <img className="message-bubble__image" src={imageUrl} alt={msg.Attachments[0].fileName} />
        </a>
      );
    }
    if (msg.messageType === "file") {
      const fileUrl = getFileUrl(msg.Attachments?.[0]);
      return (
        <a className="message-bubble__file" href={fileUrl} target="_blank" rel="noopener noreferrer">
          <FileIcon sx={{ fontSize: 24 }} />
          <div>
            <span className="message-bubble__file-name">{msg.content || msg.Attachments?.[0]?.fileName}</span>
            {msg.Attachments?.[0]?.fileSize && (
              <span className="message-bubble__file-size">{(msg.Attachments[0].fileSize / 1024).toFixed(1)} KB</span>
            )}
          </div>
        </a>
      );
    }
    return <span className="message-bubble__content">{msg.content}</span>;
  };

  return (
    <div className="message-thread">
      {/* Header */}
      <div className="message-thread__header">
        <CustomAvatar size="lg" backgroundColor="var(--color-brand-primary-purple-600)">
          {otherInfo ? getInitials(otherInfo.firstName, otherInfo.lastName) : "?"}
        </CustomAvatar>
        <div className="message-thread__header-info">
          <span className="message-thread__header-name">
            {otherInfo ? `${otherInfo.firstName} ${otherInfo.lastName}` : "Unknown"}
          </span>
          <span
            className="message-thread__header-status"
            style={{
              color: otherOnline ? "var(--color-primitives-success-600)" : "var(--color-brand-primary-grey-500)",
            }}
          >
            {getStatusText()}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="message-thread__messages" ref={messagesContainerRef}>
        {messages.length < messagesTotalCount && (
          <div className="load-more-btn">
            <CustomButton label="Load earlier messages" variant="text" size="small" onClick={handleLoadMore} />
          </div>
        )}

        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <React.Fragment key={date}>
            <div className="date-header-wrapper">
              <span className="date-header-chip">{formatDateHeader(msgs[0].createdAt)}</span>
            </div>
            {msgs.map((msg) => {
              const sent = isSentByMe(msg);
              const reactions = msg.Reactions || [];
              const groupedReactions = getGroupedReactions(reactions);

              return (
                <div
                  key={msg.messageId}
                  className={`message-bubble ${sent ? "message-bubble--sent" : "message-bubble--received"}`}
                >
                  {msg.ParentMessage && !msg.ParentMessage.isDeleted && (
                    <div className="message-bubble__reply-preview">{msg.ParentMessage.content}</div>
                  )}

                  {renderMessageContent(msg)}

                  {!msg.isDeleted && (
                    <div className="message-bubble__meta">
                      {msg.isEdited && <span className="message-bubble__edited">edited</span>}
                      <span className="message-bubble__time">{formatMessageTime(msg.createdAt)}</span>
                      {sent &&
                        (msg.ReadReceipts?.length > 0 ? (
                          <DoneAllIcon
                            sx={{
                              fontSize: 14,
                              color: sent ? "rgba(255,255,255,0.8)" : "var(--color-brand-primary-purple-600)",
                            }}
                          />
                        ) : (
                          <DoneIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                        ))}
                    </div>
                  )}

                  {Object.keys(groupedReactions).length > 0 && (
                    <div className="message-bubble__reactions">
                      {Object.entries(groupedReactions).map(([emoji, reactors]) => (
                        <CustomTooltip
                          key={emoji}
                          content={reactors.map((r) => userNameMap[r.userId] || r.userId).join(", ")}
                        >
                          <span
                            className="message-bubble__reaction"
                            onClick={() => handleReaction(msg.messageId, emoji)}
                          >
                            {emoji}
                            <span className="message-bubble__reaction-count">{reactors.length}</span>
                          </span>
                        </CustomTooltip>
                      ))}
                    </div>
                  )}

                  {!msg.isDeleted && (
                    <div className="message-bubble__actions">
                      {EMOJI_LIST.map((emoji) => (
                        <span
                          key={emoji}
                          className="message-bubble__action-emoji"
                          onClick={() => handleReaction(msg.messageId, emoji)}
                          role="button"
                        >
                          {emoji}
                        </span>
                      ))}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setSelectedMessage(msg);
                          setMenuAnchorEl(e.currentTarget);
                        }}
                        sx={{ padding: "2px" }}
                      >
                        <MoreHorizIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {someoneTyping && <div className="message-thread__typing">Someone is typing...</div>}

      <div className="message-thread__input">
        {replyTo && (
          <div className="message-input__reply-preview">
            <span className="paragraph text-sm">
              Replying to: {replyTo.content?.substring(0, 50)}
              {(replyTo.content?.length || 0) > 50 ? "..." : ""}
            </span>
            <IconButton size="small" onClick={() => setReplyTo(null)}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        )}

        {fileError && (
          <div className="message-input__file-error">
            <span>{fileError}</span>
            <IconButton size="small" onClick={() => setFileError(null)}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </div>
        )}

        {editingMessage && (
          <div className="message-input__reply-preview">
            <span className="paragraph text-sm">Editing message</span>
            <IconButton
              size="small"
              onClick={() => {
                setEditingMessage(null);
                setInputValue("");
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        )}

        {uploadingFile && (
          <div className="message-input__uploading">
            <CircularProgress size={14} sx={{ color: "var(--color-brand-primary-purple-600)" }} />
            <span>Uploading file...</span>
          </div>
        )}

        <div className="message-input">
          <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile}
            sx={{ color: "var(--color-brand-primary-purple-600)" }}
          >
            {uploadingFile ? (
              <CircularProgress size={20} sx={{ color: "var(--color-brand-primary-purple-600)" }} />
            ) : (
              <AttachFileIcon />
            )}
          </IconButton>

          <Text
            name="messageInput"
            className="message-input__field"
            size="small"
            variant="filled"
            placeholder="Type a message..."
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            hideHelperText
          />

          <IconButton
            className="message-input__send-btn"
            onClick={handleSend}
            disabled={!inputValue.trim() && !editingMessage}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </div>
      </div>

      <Menu
        open={menuAnchorEl !== null}
        onClose={handleCloseMenu}
        anchorEl={menuAnchorEl}
        autoWidth
        width="auto"
        minWidth="80px"
        elevation={4}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
      >
        <MenuItem
          value="Reply"
          onClick={handleReply}
          isDense
          className="chat-compact-menu-item"
          leftSlot={<ReplyIcon sx={{ fontSize: 14 }} />}
        />
        {selectedMessage && isSentByMe(selectedMessage) && (
          <MenuItem
            value="Edit"
            onClick={handleEdit}
            isDense
            className="chat-compact-menu-item"
            leftSlot={<EditIcon sx={{ fontSize: 14 }} />}
          />
        )}
        {selectedMessage && isSentByMe(selectedMessage) && (
          <MenuItem
            value="Delete"
            onClick={handleDelete}
            variant="danger"
            isDense
            className="chat-compact-menu-item"
            leftSlot={<DeleteIcon sx={{ fontSize: 14 }} />}
          />
        )}
      </Menu>
    </div>
  );
}

export default MessageThread;
